import { Colors } from '@pinchenterprisesnpm/friday-ui'
import {
  faChartLineUp,
  faCircleCheck,
} from '@fortawesome/pro-regular-svg-icons'
import { UNMAPPED_COLORS } from '@src/helpers/constants/colors'
import { ISuggestedPayRateStrategy } from '@network/models/suggested-pay-rates.model'
import { checkIfShiftIsWithin24hours } from '@src/helpers/helperFuncs'
import { SHIFT_TYPE } from '@network/models/shift.model'
import { parse } from 'date-fns'
import { TIME_FORMAT_STRING } from '@src/helpers/timeAndDateHelpers/constants'
import { IStrategySelectionShiftParams } from './smart-pay.types'

export const getSmartPaySetup = (
  payValue: number,
  strategy: ISuggestedPayRateStrategy,
) => {
  // Red
  if (payValue <= strategy.pay_rate_scale.red.to) {
    return {
      bgColor: UNMAPPED_COLORS.lightRedGradient,
      messageColor: UNMAPPED_COLORS.darkRedShade,
      barColor: Colors.danger,
      tagline: strategy.pay_rate_scale.red.tagline,
      bigTag: null,
    }
  }

  // Yellow
  if (payValue <= strategy.pay_rate_scale.yellow.to) {
    return {
      bgColor: UNMAPPED_COLORS.lightYellowShade,
      messageColor: UNMAPPED_COLORS.darkYellowShade,
      barColor: Colors.warning,
      tagline: strategy.pay_rate_scale.yellow.tagline,
      bigTag: {
        text: 'Increase Rate',
        icon: faChartLineUp,
        bgColor: UNMAPPED_COLORS.lightYellowShade2,
      },
    }
  }

  // Green
  if (payValue <= strategy.pay_rate_scale.green.to) {
    return {
      bgColor: UNMAPPED_COLORS.lightGreenShade,
      messageColor: UNMAPPED_COLORS.darkGreenShade,
      barColor: Colors.success,
      tagline: strategy.pay_rate_scale.green.tagline,
      bigTag: {
        text: 'Competitive',
        icon: faCircleCheck,
        bgColor: UNMAPPED_COLORS.lightGreenShade2,
      },
    }
  }

  // Deep Green
  return {
    bgColor: UNMAPPED_COLORS.lightGreenShade,
    messageColor: UNMAPPED_COLORS.darkGreenShade,
    barColor: Colors.success,
    tagline: strategy.pay_rate_scale.deep_green.tagline,
    bigTag: {
      text: 'Premium Rate',
      icon: faChartLineUp,
      bgColor: UNMAPPED_COLORS.lightGreenShade2,
    },
  }
}

export const calculatePercentage = (
  payValue: number,
  minPayRate: number,
  recommendedPay: number,
) => {
  const val = Number(payValue)
  const min = Number(minPayRate)
  const max = Number(recommendedPay)

  // Prevent division by zero or invalid ranges
  if (max <= min) return 0

  // If the value is strictly less than the minimum, return 0%
  if (val < min) return 0

  const calculated = ((val - min) / (max - min)) * 100

  // Since val is >= min, ensure the rounded result is at least 1%
  return Math.max(1, Math.round(calculated))
}

export const getSelectedPayRateStrategy = (
  regularStrategy: ISuggestedPayRateStrategy,
  lastMinuteStrategy: ISuggestedPayRateStrategy,
  strategySelectionParams: IStrategySelectionShiftParams,
): ISuggestedPayRateStrategy => {
  const {
    propertyTimezone,
    shiftType,
    singleDayDate,
    hourStart,
    days,
  } = strategySelectionParams

  // If no property timezone, return regular strategy
  if (!propertyTimezone) {
    return regularStrategy
  }

  let date = null

  // If single day shift, parse the hour start and single day date
  if (shiftType === SHIFT_TYPE.SINGLEDAY && singleDayDate && hourStart) {
    date = parse(hourStart, TIME_FORMAT_STRING.hh_mm_a, singleDayDate)
  }

  // If multiday shift, parse the first working date and hour start
  if (shiftType === SHIFT_TYPE.MULTIDAY && days && days.length > 0) {
    const firstWorkingDate = days.find(day => day.working)
    if (firstWorkingDate) {
      const originalDate = firstWorkingDate.date_start
      const timeString = firstWorkingDate.hour_start

      // Creates a new Date object using originalDate as the base
      date = parse(timeString, TIME_FORMAT_STRING.hh_mm_a, originalDate)
    }
  }

  // If no date, return regular strategy
  if (!date) {
    return regularStrategy
  }

  const isShiftWithin24hours = checkIfShiftIsWithin24hours(
    date,
    propertyTimezone,
  )

  return isShiftWithin24hours ? lastMinuteStrategy : regularStrategy
}
